import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Calendar, MapPin, Clock, Video, CheckCircle, XCircle } from "lucide-react"; // Added checkmark icons

const platformNames = {
  1: "Twitch",
  2: "Youtube",
  3: "Facebook",
  4: "Twitter",
  5: "LinkedIn",
  6: "Custom RTMP",
};

const roleOptions = [
  { value: "1", label: "Director" },
  { value: "2", label: "Camera Operator" },
  { value: "3", label: "Audio Engineer" },
  { value: "4", label: "Lighting Technician" },
  { value: "5", label: "Video Editor" },
  { value: "6", label: "Stream Engineer" },
  { value: "7", label: "Production Manager" },
  { value: "8", label: "Graphics Designer" },
  { value: "9", label: "Videographer" },
  { value: "10", label: "Photographers" },
];

const StatusBadge = ({ project }) => {
  const styles = {
    Active: "text-green-600 bg-green-100 border border-green-300",
    Completed: "text-blue-600 bg-blue-100 border border-blue-300",
    Cancelled: "text-red-600 bg-red-100 border border-red-300",
    Draft: "text-yellow-600 bg-yellow-100 border border-yellow-300",
  };

  const getStatus = () => {
    if (project?.is_completed) return "Completed";
    if (project?.is_cancelled) return "Cancelled";
    if (project?.is_draft) return "Draft";
    if (project?.is_active) return "Active";
    return "Unknown";
  };

  const status = getStatus();

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
};

const ProjectDetailsModal = ({ open, onOpenChange, project }) => {
  if (!project) return null;

  const streamingPlatforms = project?.project?.streaming_platforms || [];
  const assignedCrew = project?.assignedCrew || [];
  const equipmentDetails = project?.equipmentDetails || [];
  const description = project?.project?.description || "";

  const fetchRole = (roleId) => {
    const roleObject = roleOptions.find(
      (role) => role.value === String(roleId)
    );
    return roleObject?.label || "";
  };

  const formatLocation = (locationInput) => {
    if (!locationInput) return "N/A";

    let addressStr = locationInput;

    try {
      const parsed = JSON.parse(locationInput);
      if (parsed && parsed.address) {
        addressStr = parsed.address;
      }
    } catch (e) { }

    const parts = addressStr.split(',').map(p => p.trim());

    if (parts.length >= 3) {
      const country = parts[parts.length - 1];
      const stateZip = parts[parts.length - 2];
      const city = parts[parts.length - 3];
      const state = stateZip.replace(/\d+/g, '').trim();

      return `${city}, ${state}, ${country}`;
    }

    return addressStr;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="fixed p-0 flex flex-col overflow-hidden bg-white dark:bg-card border-none z-50
         /* MOBILE STYLES: Bottom Sheet */
         /* Added !top-auto and !translate-y-0 to prevent the button from being pushed down */
         bottom-0 left-0 right-0 w-full !h-[70dvh] rounded-t-[20px]
         !top-auto !translate-y-0 translate-x-0
         data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom

         /* DESKTOP STYLES: Right Drawer */
         md:top-0 md:right-0 md:left-auto md:!h-full md:w-[450px] md:rounded-none md:max-w-none
         md:!translate-x-0 md:!translate-y-0
         md:data-[state=open]:slide-in-from-right md:data-[state=open]:slide-in-from-bottom-0"
      >
        {/* MOBILE DRAG HANDLE (Visible only on mobile) */}
        <div className="w-12 h-1.5 bg-[#79747E] rounded-full mx-auto mt-3 shrink-0 md:hidden" />

        {/* HEADER (NON-SCROLLABLE) */}
        <div className="p-6 border-b border-border shrink-0">
          <DialogHeader className={"text-left"}>
            <div className="mb-2">
              <StatusBadge project={project.project} />
            </div>

            <div className="flex items-center gap-3 mt-4">
              <div className="flex flex-col text-left">
                <span className="text-lg font-semibold dark:text-foreground">
                  {project.project.project_name}
                </span>

                <div className="flex flex-wrap gap-2 md:gap-4 text-sm text-muted-foreground mt-2">
                  <div className="flex items-center gap-2">
                    <Calendar size={16} /> {project.project.event_date}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={16} /> {formatLocation(project.project.event_location)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={16} /> {project.project.duration_hours} hours
                  </div>
                </div>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* SCROLLABLE CONTENT */}
        <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-4 space-y-6">
          {/* Description */}
          {description && (
            <div>
              <p className="text-xs font-semibold mb-1 text-muted-foreground">Description</p>
              <p className="text-sm dark:text-foreground">{description}</p>
            </div>
          )}

          {/* Streaming Platforms */}
          {streamingPlatforms?.length !== 0 && (
            <div>
              <p className="text-xs font-semibold mb-2 text-muted-foreground">Streaming Platforms</p>
              <div className="flex flex-wrap gap-2">
                {(Array.isArray(streamingPlatforms)
                  ? streamingPlatforms
                  : JSON.parse(streamingPlatforms || "[]")
                ).map((platformId, index) => (
                  <span
                    key={index}
                    className="border dark:border-border px-3 py-1 rounded-full bg-muted dark:bg-secondary/20 text-sm flex items-center gap-2 dark:text-foreground"
                  >
                    <Video size={14} className="dark:text-primary" /> {platformNames[platformId]}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Assigned Crew */}
          {assignedCrew?.length !== 0 && (
            <section className="border rounded-xl p-4 space-y-4">
              <p className="text-xs font-semibold text-muted-foreground">
                Assigned Crew ({assignedCrew.length}/10)
              </p>
              <div className="flex gap-3 overflow-x-auto scrollbar-hidden no-scrollbar">
                {assignedCrew.map((crew, index) => (
                  <div key={index} className="border dark:border-border p-3 rounded-xl w-40 shrink-0 flex gap-2 dark:bg-secondary/10">
                    <img src="https://i.pravatar.cc/40" className="w-10 h-10 rounded-full" alt="avatar" />
                    <div>
                      <p className="text-sm font-medium dark:text-foreground">{crew.crew_member.first_name} {crew.crew_member.last_name}</p>
                      <p className="text-xs dark:text-muted-foreground">{fetchRole(crew.crew_member?.primary_role)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Equipment Details */}
          {equipmentDetails?.length !== 0 && (
            <section className="border rounded-xl p-4 space-y-4">
              <p className="text-xs font-semibold text-muted-foreground">Equipment Details ({equipmentDetails.length}/10)</p>
              <div className="flex gap-3 overflow-x-auto scrollbar-hidden no-scrollbar">
                {equipmentDetails.map((equipment, index) => (
                  <div key={index} className="border dark:border-border bg-muted dark:bg-secondary/10 p-3 rounded-xl w-40 shrink-0 flex flex-col items-center">
                    <p className="text-sm font-medium dark:text-foreground">{equipment.equipment_name}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {equipment.is_assigned ? (
                        <CheckCircle size={16} className="text-green-600" />
                      ) : (
                        <XCircle size={16} className="text-red-600" />
                      )}
                      <span className="text-xs text-muted-foreground">{equipment.is_assigned ? "Assigned" : "Not Assigned"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Time & Budget */}
          <section className="border rounded-xl p-4">
            <p className="text-xs font-semibold text-muted-foreground mb-3">
              Time & Budget
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Duration</p>
                <p className="font-semibold dark:text-foreground">{project.project.duration_hours} Hrs</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Budget</p>
                <p className="font-semibold dark:text-foreground">${project.project.budget}</p>
              </div>
            </div>
          </section>
        </div>

        {/* FIXED BOTTOM FOOTER (Visible only on mobile to match image) */}
        <div className="p-4 bg-white dark:bg-card md:hidden shrink-0">
          <button
            onClick={() => onOpenChange(false)}
            className="w-full py-3 bg-muted hover:bg-muted/80 text-foreground text-sm font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectDetailsModal;
